import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IconChevronRight, IconPrescription, IconX } from "@tabler/icons-react";
import { documentService } from "@/services/documents.service";
import { getMemberInfo } from "@/hooks/useMembers";
import { NAVIGATE_BACK_IN_HISTORY } from "@/hooks/useBackNavigation";
import { PillboxPendingChange } from "@/types/pillbox";
import {
  formatDisplayDate,
  formatDoctorDateBadge,
} from "./utils/formatMedicine";
import styles from "./ViewPrescriptions.module.css";

interface PrescriptionSummary {
  sourcePrescriptionId: string;
  sourceDocumentId: string;
  doctorName?: string;
  dateOfVisit?: string;
}

const ViewPrescriptions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";

  const [openingId, setOpeningId] = useState<string | null>(null);

  const prescriptions = useMemo<PrescriptionSummary[]>(() => {
    const pending = (location.state?.pending as PillboxPendingChange[]) ?? [];
    const map = new Map<string, PrescriptionSummary>();
    for (const item of pending) {
      if (!map.has(item.sourcePrescriptionId)) {
        map.set(item.sourcePrescriptionId, {
          sourcePrescriptionId: item.sourcePrescriptionId,
          sourceDocumentId: item.sourceDocumentId,
          doctorName: item.doctorName,
          dateOfVisit: item.dateOfVisit,
        });
      }
    }
    return Array.from(map.values());
  }, [location.state]);

  const handleOpen = async (prescription: PrescriptionSummary) => {
    if (openingId) return;
    setOpeningId(prescription.sourcePrescriptionId);
    try {
      const document = await documentService(memberId).getById(
        prescription.sourceDocumentId
      );
      navigate("/documents/view", {
        state: {
          document,
          // This screen's own history entry already holds location.state
          // (the pending list) — pop back to it via navigate(-1) instead of
          // pushing a reconstructed copy, which grew the history stack on
          // every Back tap and produced a Back/Back navigation loop.
          backUrl: NAVIGATE_BACK_IN_HISTORY,
        },
      });
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => navigate(-1)}
            aria-label={t("common.close")}
          >
            <IconX size={24} />
          </button>
        </div>
        <div className={styles.textHeader}>
          <h1 className={styles.title}>
            {t("pillbox.review.prescriptionsTitle")}
          </h1>
          <p className={styles.subtitle}>
            {t("pillbox.review.prescriptionsSubtitle")}
          </p>
        </div>
      </div>

      <div className={styles.list}>
        {prescriptions.map((prescription) => {
          // Empty when neither doctor nor visit date is known — the badge is
          // then omitted rather than showing a placeholder.
          const doctorDateBadge = formatDoctorDateBadge(
            prescription.doctorName,
            prescription.dateOfVisit
              ? formatDisplayDate(prescription.dateOfVisit)
              : undefined
          );
          return (
            <button
              type="button"
              key={prescription.sourcePrescriptionId}
              className={styles.card}
              onClick={() => handleOpen(prescription)}
              disabled={openingId === prescription.sourcePrescriptionId}
            >
              <div className={styles.cardContent}>
                {doctorDateBadge && (
                  <span className={styles.doctorDate}>{doctorDateBadge}</span>
                )}
                <span className={styles.metaRow}>
                  <span className={styles.metaLabel}>
                    <IconPrescription size={12} />
                    {t("pillbox.review.prescriptionWord")}
                  </span>
                  {prescription.dateOfVisit && (
                    <span className={styles.metaDate}>
                      {t("pillbox.review.prescriptionDated", {
                        date: formatDisplayDate(prescription.dateOfVisit),
                      })}
                    </span>
                  )}
                </span>
              </div>
              <IconChevronRight size={24} className={styles.chevron} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ViewPrescriptions;
